import React, { useState, useEffect } from 'react';
import { 
  DataGridPro,
  GridToolbarContainer, 
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExportContainer,
  GridToolbarQuickFilter,
  GridCsvExportMenuItem
} from '@mui/x-data-grid-pro';
import {
  Box,
  Typography,
  Alert,
  Snackbar,
  Chip,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Card,
  CardContent,
  CircularProgress,
  LinearProgress,
  Tooltip,
  Avatar
} from '@mui/material';
import { 
  Warning as WarningIcon,
  CalendarMonth as CalendarMonthIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import indexedDBService from '../services/indexedDBService';
import bookingPredictionService from '../services/bookingPredictionService';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../utils/dateUtils';

// Target industry sectors - using Salesforce API names (not labels)
// These match the Industry_Sectors__c picklist API values from Salesforce
const TARGET_INDUSTRIES = [
  'Fashion/Apparel/Accessories', // API name for "Fashion (inc Shoes, Accessories, Children)"
  'Furniture', // API name for "Furniture & Homewares"
  'Beauty/Cosmetics', // API name matches label
  'Major Retail/Department Store/Supermarket', // API name for "Retail & Consumer Products"
  'Health & Wellness', // API name for "Health, Sport & Fitness"
  // Include variations for backward compatibility and fuzzy matching
  'Fashion',
  'Furniture & Homewares',
  'Homewares',
  'Retail & Consumer Products',
  'Retail',
  'Consumer Products',
  'Beauty',
  'Cosmetics'
];

// Helper function to check if a client matches target industries (fuzzy matching)
const matchesTargetIndustry = (clientIndustry) => {
  if (!clientIndustry || clientIndustry === 'N/A') return false;
  
  const industries = clientIndustry.split(',').map(i => i.trim());
  
  // Check for exact matches
  if (industries.some(ind => TARGET_INDUSTRIES.includes(ind))) {
    return true;
  }
  
  // Check for partial matches (case-insensitive)
  const lowerIndustry = clientIndustry.toLowerCase();
  const targetKeywords = ['fashion', 'furniture', 'homewares', 'beauty', 'cosmetics', 'retail', 'consumer'];
  
  return targetKeywords.some(keyword => lowerIndustry.includes(keyword));
};

const ClientWatchlist = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingStatus, setLoadingStatus] = useState('Loading client data...');
  const [emailFiltering, setEmailFiltering] = useState(false);
  const [error, setError] = useState(null);
  
  // Filters
  const [industryFilter, setIndustryFilter] = useState('all');
  const [patternFilter, setPatternFilter] = useState('all');
  const [overdueFilter, setOverdueFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [clients, industryFilter, patternFilter, overdueFilter, ownerFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      setLoadingStatus('Loading client data from database...');

      // Get all clients from IndexedDB
      const allClients = await indexedDBService.getAllClients();
      
      // Debug: Log Universal Store if present
      const universalStore = allClients.find(c => 
        c.clientName && c.clientName.toLowerCase().includes('universal')
      );
      if (universalStore) {
        // Check what stages are present
        const stages = {};
        universalStore.opportunities?.forEach(opp => {
          const stage = opp.StageName || 'Unknown';
          stages[stage] = (stages[stage] || 0) + 1;
        });
        
        console.log('Universal Store found:', {
          name: universalStore.clientName,
          industry: universalStore.industry,
          totalOpportunityCount: universalStore.opportunities?.length || 0,
          matchesIndustry: matchesTargetIndustry(universalStore.industry),
          stages: stages,
          opportunitiesInLast12Months: universalStore.opportunities?.filter(opp => {
            const oppDate = new Date(opp.CreatedDate || opp.CloseDate);
            const twelveMonthsAgo = new Date();
            twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
            return oppDate >= twelveMonthsAgo;
          }).length || 0
        });
      }
      
      // Filter by target industries (using fuzzy matching)
      const targetClients = allClients.filter(client => {
        return matchesTargetIndustry(client.industry);
      });
      
      console.log(`Filtered ${targetClients.length} clients from ${allClients.length} total clients`);
      setLoadingStatus(`Processing ${targetClients.length} clients and analyzing patterns...`);

      // Calculate date 24 months ago (2 years) for better pattern detection
      const twoYearsAgo = new Date();
      twoYearsAgo.setMonth(twoYearsAgo.getMonth() - 24);

      // Process each client for pattern analysis
      const processedClients = targetClients.map((client) => {
        // Get all opportunities (any StageName) sorted by CreatedDate
        // Note: If CreatedDate is not available, we'll use CloseDate as fallback
        // Filter to last 2 years (24 months) for better pattern detection
        const opportunities = (client.opportunities || [])
          .filter(opp => {
            // Filter out opportunities without dates
            if (!opp.CreatedDate && !opp.CloseDate) return false;
            
            // Use CreatedDate if available, otherwise fallback to CloseDate
            const oppDate = new Date(opp.CreatedDate || opp.CloseDate);
            
            // Include opportunities from the last 24 months (2 years)
            return oppDate >= twoYearsAgo;
          })
          .map(opp => ({
            ...opp,
            // Use CreatedDate if available, otherwise fallback to CloseDate
            CreatedDate: opp.CreatedDate || opp.CloseDate,
            // Keep original for reference
            originalCreatedDate: opp.CreatedDate,
            originalCloseDate: opp.CloseDate
          }))
          .sort((a, b) => {
            const dateA = new Date(a.CreatedDate);
            const dateB = new Date(b.CreatedDate);
            return dateA - dateB;
          });

        // Analyze enquiry pattern
        const patternAnalysis = bookingPredictionService.analyzeBookingPattern(opportunities);
        
        // Predict next enquiry
        const prediction = bookingPredictionService.predictNextBooking({
          patternAnalysis
        });

        // Check if overdue for enquiry
        const overdueStatus = bookingPredictionService.isOverdue({
          patternAnalysis
        });

        return {
          id: client.accountId,
          accountId: client.accountId,
          clientName: client.clientName || 'N/A',
          industry: client.industry || 'N/A',
          owner: client.owner || {},
          patternAnalysis,
          prediction,
          overdueStatus,
          opportunityCount: opportunities.length,
          lastBookingDate: patternAnalysis.lastBookingDate // Keep field name for compatibility, but display as "Last Enquiry"
        };
      });

      // Debug: Check Universal Store after processing
      const universalStoreProcessed = processedClients.find(c => 
        c.clientName && c.clientName.toLowerCase().includes('universal')
      );
      if (universalStoreProcessed) {
        console.log('Universal Store after processing:', {
          name: universalStoreProcessed.clientName,
          opportunityCount: universalStoreProcessed.opportunityCount,
          pattern: universalStoreProcessed.patternAnalysis.pattern
        });
      }

      // Filter out clients with less than 2 opportunities
      setLoadingStatus(`Filtering clients with sufficient data (need at least 2 enquiries)...`);
      const clientsWithSufficientData = processedClients.filter(
        client => client.opportunityCount >= 2
      );
      
      console.log(`After filtering <2 opportunities: ${clientsWithSufficientData.length} clients`);
      setLoadingStatus(`Found ${clientsWithSufficientData.length} clients with sufficient data. Identifying overdue clients...`);

      // Show results immediately (don't block on email checking)
      // Email checking will happen asynchronously in the background
      setLoadingStatus('Finalizing results...');
      setClients(clientsWithSufficientData);
      setLoading(false); // Show results immediately - don't wait for email checks

      // Check for recent email activity asynchronously (in background)
      // This allows the UI to be responsive while email checking happens
      const checkEmailActivityAsync = async () => {
        try {
          // Prepare account IDs for email activity check (only overdue clients need checking)
          const overdueAccountIds = clientsWithSufficientData
            .filter(client => client.overdueStatus?.isOverdue)
            .map(client => client.accountId);
          
          if (overdueAccountIds.length === 0) {
            return; // No overdue clients to check
          }

          setEmailFiltering(true); // Show indicator that email filtering is happening
          
          // First, check cache for email activity (24 hour TTL)
          console.log(`Checking cached email activity for ${overdueAccountIds.length} overdue clients...`);
          const cachedEmailActivity = await indexedDBService.getCachedEmailActivity(overdueAccountIds, 24 * 60 * 60 * 1000);
          
          // Find account IDs that need fresh data (not in cache or expired)
          const accountIdsToCheck = overdueAccountIds.filter(accountId => !cachedEmailActivity.hasOwnProperty(accountId));
          
          let emailActivityMap = { ...cachedEmailActivity };
          
          // Only fetch from API if we have accounts that aren't cached
          if (accountIdsToCheck.length > 0) {
            console.log(`Fetching fresh email activity for ${accountIdsToCheck.length} accounts (${overdueAccountIds.length - accountIdsToCheck.length} from cache)...`);
            
            const emailCheckResponse = await fetch('/api/sf/check-email-activity', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                accountIds: accountIdsToCheck
              })
            });

            if (emailCheckResponse.ok) {
              const emailCheckData = await emailCheckResponse.json();
              const freshEmailActivity = emailCheckData.results || {};
              
              // Merge fresh data with cached data
              emailActivityMap = { ...emailActivityMap, ...freshEmailActivity };
              
              // Cache the fresh results
              await indexedDBService.cacheEmailActivity(freshEmailActivity);
            } else {
              // If email check fails, use cached data only (fail-safe)
              console.warn('Email activity check failed, using cached data only');
            }
          } else {
            console.log(`Using cached email activity for all ${overdueAccountIds.length} accounts`);
          }
          
          // Attach email activity details to all clients (for display purposes only)
          // Note: We do NOT filter out clients here - filtering only happens in the weekly report
          const clientsWithEmailInfo = clientsWithSufficientData.map(client => {
            // Attach email activity details to client object (for display in the table)
            const emailActivity = emailActivityMap[client.accountId];
            if (emailActivity && emailActivity.hasEmails) {
              client.emailActivity = emailActivity;
              client.hasRecentEmailActivity = emailActivity.isRecent || false; // Flag for display only
            } else {
              client.hasRecentEmailActivity = false;
            }
            return client;
          });

          console.log(`Email activity data attached to ${clientsWithEmailInfo.length} clients (no filtering applied)`);
          
          // Update clients with email activity data (this will trigger a re-render)
          // Note: All clients remain visible - filtering only happens in weekly report
          setClients(clientsWithEmailInfo);
        } catch (emailCheckError) {
          // If email check fails, keep existing clients (fail-safe)
          console.error('Error checking email activity:', emailCheckError);
        } finally {
          setEmailFiltering(false); // Hide indicator when done
        }
      };

      // Start email checking in background (don't await - fire and forget)
      checkEmailActivityAsync();
    } catch (err) {
      console.error('Error loading client watchlist data:', err);
      setError(err.message || 'Failed to load client data');
      setLoading(false);
    }
    // Note: Email checking happens async above, so we don't set loading to false here
  };

  const applyFilters = () => {
    let filtered = [...clients];

    // Filter by industry
    if (industryFilter !== 'all') {
      filtered = filtered.filter(client => {
        const industries = client.industry.split(',').map(i => i.trim());
        return industries.includes(industryFilter);
      });
    }

    // Filter by pattern type
    if (patternFilter !== 'all') {
      filtered = filtered.filter(client => {
        return client.patternAnalysis.pattern === patternFilter;
      });
    }

    // Filter by overdue status
    if (overdueFilter === 'overdue') {
      filtered = filtered.filter(client => client.overdueStatus.isOverdue);
    } else if (overdueFilter === 'approaching') {
      filtered = filtered.filter(client => client.overdueStatus.status === 'approaching');
    } else if (overdueFilter === 'on_track') {
      filtered = filtered.filter(client => client.overdueStatus.status === 'on_track');
    }

    // Filter by account owner
    if (ownerFilter !== 'all') {
      filtered = filtered.filter(client => {
        const ownerName = client.owner?.name || 'N/A';
        return ownerName === ownerFilter;
      });
    }

    setFilteredClients(filtered);
  };

  const getPatternColor = (pattern) => {
    const colors = {
      monthly: 'primary',
      quarterly: 'secondary',
      bi_monthly: 'info',
      semi_annual: 'warning',
      annual: 'success',
      custom: 'default',
      insufficient_data: 'error'
    };
    return colors[pattern] || 'default';
  };

  const getConfidenceColor = (confidence) => {
    const colors = {
      high: 'success',
      medium: 'warning',
      low: 'error'
    };
    return colors[confidence] || 'default';
  };

  const columns = [
    {
      field: 'owner',
      headerName: 'Account Owner',
      width: 180,
      valueGetter: (value, row) => {
        // Return owner name for sorting
        return row.owner?.name || 'N/A';
      },
      renderCell: (params) => {
        const owner = params.row.owner;
        const sfBaseUrl = process.env.REACT_APP_SALESFORCE_URL || 'https://purelocations.lightning.force.com';
        // Try the content delivery network path
        const photoUrl = owner?.photoUrl?.replace(
          'https://purelocations.file.force.com/profilephoto/',
          `${sfBaseUrl}/content/images/`
        );

        return (
          <Box 
            display="flex" 
            alignItems="center" 
            gap={1}
            sx={{
              height: '100%',
              py: 0,
              my: 0,
              justifyContent: 'flex-start',
              verticalAlign: 'middle'
            }}
          >
            <Avatar 
              src={photoUrl} 
              sx={{ 
                width: 24, 
                height: 24,
                bgcolor: 'grey.400',
                flexShrink: 0,
                fontSize: '0.75rem',
                m: 0
              }}
            >
              {owner?.name?.charAt(0) || 'N/A'}
            </Avatar>
            <Box
              component="span"
              sx={{
                lineHeight: 'inherit',
                display: 'inline-flex',
                alignItems: 'center',
                m: 0,
                p: 0,
                verticalAlign: 'middle'
              }}
            >
              {owner?.name || 'N/A'}
            </Box>
          </Box>
        );
      }
    },
    {
      field: 'clientName',
      headerName: 'Client Name',
      width: 200,
      renderCell: (params) => {
        // Get the full client data from IndexedDB to pass to ClientDetails
        const handleClick = async () => {
          try {
            const clientData = await indexedDBService.getClientById(params.row.accountId);
            if (clientData) {
              navigate(`/clients/${params.row.accountId}`, {
                state: { client: clientData }
              });
            } else {
              // Fallback: navigate without state if client not found in IndexedDB
              navigate(`/clients/${params.row.accountId}`);
            }
          } catch (error) {
            console.error('Error fetching client data:', error);
            navigate(`/clients/${params.row.accountId}`);
          }
        };
        
        return (
          <Box
            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            onClick={handleClick}
          >
            {params.value}
          </Box>
        );
      }
    },
    {
      field: 'industry',
      headerName: 'Industry',
      width: 250,
      valueGetter: (value) => {
        if (!value) return 'N/A';
        const industries = value.split(',').map(i => i.trim());
        return industries.filter(ind => TARGET_INDUSTRIES.includes(ind)).join(', ') || value;
      }
    },
    {
      field: 'pattern',
      headerName: 'Pattern',
      width: 150,
      valueGetter: (value, row) => {
        // Return pattern for sorting (alphabetical)
        return row.patternAnalysis?.pattern || 'insufficient_data';
      },
      renderCell: (params) => {
        const pattern = params.row.patternAnalysis.pattern;
        const description = params.row.patternAnalysis.patternDescription;
        return (
          <Tooltip title={description}>
            <Chip
              label={description}
              color={getPatternColor(pattern)}
              size="small"
            />
          </Tooltip>
        );
      }
    },
    {
      field: 'confidence',
      headerName: 'Confidence',
      width: 120,
      valueGetter: (value, row) => {
        // Return numeric value for sorting: high=3, medium=2, low=1
        const confidence = row.patternAnalysis?.confidence || 'low';
        const order = { high: 3, medium: 2, low: 1 };
        return order[confidence] || 1;
      },
      renderCell: (params) => {
        const confidence = params.row.patternAnalysis.confidence;
        return (
          <Chip
            label={confidence.toUpperCase()}
            color={getConfidenceColor(confidence)}
            size="small"
            variant="outlined"
          />
        );
      }
    },
    {
      field: 'lastBookingDate',
      headerName: 'Last Enquiry',
      width: 130,
      valueGetter: (value, row) => {
        // Return timestamp for proper date sorting
        const date = row.lastBookingDate;
        return date ? new Date(date).getTime() : 0;
      },
      renderCell: (params) => {
        const date = params.row.lastBookingDate;
        if (!date) return 'N/A';
        return formatDate(date);
      }
    },
    {
      field: 'predictedNextBooking',
      headerName: 'Predicted Next Enquiry',
      width: 150,
      valueGetter: (value, row) => {
        // Return timestamp for proper date sorting, or 0 for N/A (will be at end when sorting descending)
        const prediction = row.prediction;
        if (!prediction || !prediction.isPredictable || !prediction.predictedDate) {
          return 0; // Put N/A at the end when sorting descending (most recent dates first)
        }
        return new Date(prediction.predictedDate).getTime();
      },
      renderCell: (params) => {
        const prediction = params.row.prediction;
        if (!prediction.isPredictable) {
          return (
            <Tooltip title="Insufficient data to predict">
              <Chip label="N/A" size="small" color="error" variant="outlined" />
            </Tooltip>
          );
        }
        return formatDate(prediction.predictedDate);
      }
    },
    {
      field: 'overdueStatus',
      headerName: 'Status',
      width: 150,
      valueGetter: (value, row) => {
        // Return numeric value for sorting: days overdue (positive) or days until due (negative)
        const status = row.overdueStatus;
        if (status.status === 'insufficient_data') {
          return -999999; // Put insufficient data at the end
        }
        if (status.isOverdue) {
          return status.daysOverdue || 0; // Positive = overdue, higher = more overdue
        }
        // For approaching/on_track, return negative days until due
        // This way overdue (positive) sorts first, then approaching, then on track
        return -(status.daysUntilDue || 0);
      },
      renderCell: (params) => {
        const status = params.row.overdueStatus;
        
        if (status.status === 'insufficient_data') {
          return (
            <Tooltip title="Need more enquiry history">
              <Chip
                icon={<InfoIcon />}
                label="Insufficient Data"
                color="default"
                size="small"
                variant="outlined"
              />
            </Tooltip>
          );
        }

        if (status.isOverdue) {
          return (
            <Tooltip title={`${status.daysOverdue} days overdue for enquiry`}>
              <Chip
                icon={<WarningIcon />}
                label={`${status.daysOverdue} days overdue`}
                color="error"
                size="small"
              />
            </Tooltip>
          );
        }

        if (status.status === 'approaching') {
          return (
            <Tooltip title={`Enquiry due in ${status.daysUntilDue} days`}>
              <Chip
                icon={<CalendarMonthIcon />}
                label={`Due in ${status.daysUntilDue}d`}
                color="warning"
                size="small"
              />
            </Tooltip>
          );
        }

        return (
          <Tooltip title={`On track - enquiry due in ${status.daysUntilDue} days`}>
            <Chip
              icon={<TrendingUpIcon />}
              label="On Track"
              color="success"
              size="small"
            />
          </Tooltip>
        );
      }
    },
    {
      field: 'opportunityCount',
      headerName: 'Total Enquiries',
      width: 130,
      type: 'number'
    },
    {
      field: 'lastEmailDate',
      headerName: 'Last Email Date',
      width: 150,
      valueGetter: (value, row) => {
        const emailActivity = row.emailActivity;
        if (!emailActivity || !emailActivity.hasEmails || !emailActivity.lastEmailDate) {
          return 0; // Put N/A at the end when sorting descending
        }
        return new Date(emailActivity.lastEmailDate).getTime();
      },
      renderCell: (params) => {
        const emailActivity = params.row.emailActivity;
        if (!emailActivity || !emailActivity.hasEmails || !emailActivity.lastEmailDate) {
          return <Chip label="N/A" size="small" color="default" variant="outlined" />;
        }
        return (
          <Tooltip title={`Subject: ${emailActivity.emailSubject || 'N/A'}`}>
            <Box>{formatDate(emailActivity.lastEmailDate)}</Box>
          </Tooltip>
        );
      }
    },
    {
      field: 'emailSender',
      headerName: 'Email Sender',
      width: 200,
      valueGetter: (value, row) => {
        const emailActivity = row.emailActivity;
        return emailActivity?.senderEmail || '';
      },
      renderCell: (params) => {
        const emailActivity = params.row.emailActivity;
        if (!emailActivity || !emailActivity.hasEmails || !emailActivity.senderEmail) {
          return <Chip label="N/A" size="small" color="default" variant="outlined" />;
        }
        return (
          <Tooltip title={`Sent from ${emailActivity.senderEmail}`}>
            <Chip 
              label={emailActivity.senderEmail} 
              size="small" 
              color="primary" 
              variant="outlined" 
            />
          </Tooltip>
        );
      }
    },
    {
      field: 'emailRecipients',
      headerName: 'Email Recipients',
      width: 250,
      valueGetter: (value, row) => {
        const emailActivity = row.emailActivity;
        return emailActivity?.recipientEmails?.join(', ') || '';
      },
      renderCell: (params) => {
        const emailActivity = params.row.emailActivity;
        if (!emailActivity || !emailActivity.hasEmails || !emailActivity.recipientEmails || emailActivity.recipientEmails.length === 0) {
          return <Chip label="N/A" size="small" color="default" variant="outlined" />;
        }
        const recipients = emailActivity.recipientEmails;
        const displayText = recipients.length > 2 
          ? `${recipients.slice(0, 2).join(', ')} +${recipients.length - 2} more`
          : recipients.join(', ');
        return (
          <Tooltip title={recipients.join(', ')}>
            <Box sx={{ fontSize: '0.875rem' }}>{displayText}</Box>
          </Tooltip>
        );
      }
    }
  ];

  const CustomToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarExportContainer>
        <GridCsvExportMenuItem />
      </GridToolbarExportContainer>
      <Box sx={{ flexGrow: 1 }} />
      <GridToolbarQuickFilter />
    </GridToolbarContainer>
  );

  // Get unique industries for filter - include all industries from clients, not just TARGET_INDUSTRIES
  // This ensures we show all industries that exist in the data, including variations like "Fashion/Apparel"
  const uniqueIndustries = Array.from(
    new Set(
      clients.flatMap(client => {
        if (!client.industry || client.industry === 'N/A') return [];
        return client.industry.split(',').map(i => i.trim());
      })
    )
  ).sort();

  // Get unique account owners for filter
  const uniqueOwners = Array.from(
    new Set(
      clients
        .map(client => client.owner?.name)
        .filter(name => name && name !== 'N/A')
    )
  ).sort();

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        p: 4,
        gap: 3
      }}>
        <CircularProgress size={56} thickness={4} />
        <Box sx={{ width: '100%', maxWidth: 400, textAlign: 'center' }}>
          <Typography variant="h6" color="primary" sx={{ mb: 1, fontWeight: 500 }}>
            {loadingStatus}
          </Typography>
          <LinearProgress 
            sx={{ 
              height: 6, 
              borderRadius: 3,
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 3
              }
            }} 
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, opacity: 0.8 }}>
            {loadingStatus.includes('email activity') 
              ? 'Checking Microsoft Graph API for sent emails...'
              : loadingStatus.includes('Processing')
              ? 'Analyzing booking patterns and calculating predictions...'
              : 'Please wait while we load your data...'
            }
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  const overdueCount = filteredClients.filter(c => c.overdueStatus.isOverdue).length;
  const approachingCount = filteredClients.filter(c => c.overdueStatus.status === 'approaching').length;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Client Watchlist
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Monitor client enquiry patterns and identify clients who are due to make an enquiry
      </Typography>
      
      {emailFiltering && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2">
              Loading email activity data in the background. The list will update automatically when complete.
            </Typography>
          </Box>
        </Alert>
      )}

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Overdue Clients
            </Typography>
            <Typography variant="h4" color="error">
              {overdueCount}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Approaching Due Date
            </Typography>
            <Typography variant="h4" color="warning.main">
              {approachingCount}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Total Clients Monitored
            </Typography>
            <Typography variant="h4">
              {filteredClients.length}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Industry</InputLabel>
          <Select
            value={industryFilter}
            label="Industry"
            onChange={(e) => setIndustryFilter(e.target.value)}
          >
            <MenuItem value="all">All Industries</MenuItem>
            {uniqueIndustries.map(industry => (
              <MenuItem key={industry} value={industry}>{industry}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Pattern</InputLabel>
          <Select
            value={patternFilter}
            label="Pattern"
            onChange={(e) => setPatternFilter(e.target.value)}
          >
            <MenuItem value="all">All Patterns</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="bi_monthly">Bi-monthly</MenuItem>
            <MenuItem value="quarterly">Quarterly</MenuItem>
            <MenuItem value="semi_annual">Semi-annual</MenuItem>
            <MenuItem value="annual">Annual</MenuItem>
            <MenuItem value="custom">Custom</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={overdueFilter}
            label="Status"
            onChange={(e) => setOverdueFilter(e.target.value)}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="overdue">Overdue</MenuItem>
            <MenuItem value="approaching">Approaching</MenuItem>
            <MenuItem value="on_track">On Track</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Account Owner</InputLabel>
          <Select
            value={ownerFilter}
            label="Account Owner"
            onChange={(e) => setOwnerFilter(e.target.value)}
          >
            <MenuItem value="all">All Owners</MenuItem>
            {uniqueOwners.map(owner => (
              <MenuItem key={owner} value={owner}>{owner}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Data Grid */}
      <Box sx={{ height: 600, width: '100%' }}>
        <DataGridPro
          rows={filteredClients}
          columns={columns}
          pageSize={25}
          rowsPerPageOptions={[25, 50, 100]}
          disableSelectionOnClick
          sortable={true}
          components={{
            Toolbar: CustomToolbar
          }}
          initialState={{
            sorting: {
              sortModel: [
                { field: 'predictedNextBooking', sort: 'desc' } // Sort by Predicted Next Enquiry Date descending (most recent first, matching weekly report)
              ]
            }
          }}
        />
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>Note:</strong> Pattern analysis requires at least 2 opportunities (enquiries) from the last 24 months (2 years). 
          Clients with insufficient data are marked accordingly. Predictions are based on historical 
          enquiry patterns from the past 24 months and may vary based on seasonal factors or business changes. 
          Analysis uses opportunity CreatedDate when available, falling back to CloseDate if needed.
          <br /><br />
          <strong>Important:</strong> This report includes opportunities of ALL stages (not just Closed Won). 
          If you're missing clients, ensure your data has been synced recently to include all opportunity stages.
        </Typography>
      </Alert>
    </Box>
  );
};

export default ClientWatchlist;
